import { supabase } from './supabase'
import type {
  AnalyseResultatInput,
  DiagnosticInput,
  DocumentAnalysisResult,
  PrescriptionInput
} from '@/types/health'
import { analyzeDocumentWithClaude } from './claude-analysis'
import { resolveAnalysisDates } from './medical-document-dates'
import {
  buildMedicalDocumentDuplicateSignature,
  computeMedicalDocumentFingerprint,
  DEFAULT_MEDICAL_DOCUMENT_UPLOAD_CONCURRENCY,
  createSignedMedicalDocumentUrl,
  isIgnoredMedicalDocumentFileCandidate,
  isIgnoredMedicalDocumentRecord,
  getMedicalDocumentFileKey,
  getMedicalDocumentSourcePath,
  getStoredMedicalDocumentFingerprint,
  removeMedicalDocumentFromPrivateBucket,
  resolveMedicalDocumentUrl,
  uploadMedicalDocumentToPrivateBucket
} from './medical-document-storage'
import { buildDocumentAnalysisMetadata } from './document-analysis-insights'
import { buildAnalyseResultatRows, syncPatientLabTestCatalog } from './lab-test-catalog'

export interface SimpleUploadResult {
  success: boolean
  documentId?: string
  storagePath?: string
  duplicate?: boolean
  error?: string
}

export interface SimpleUploadProgress {
  fileKey: string
  fileName: string
  progress: number
  status: 'pending' | 'checking' | 'uploading' | 'analyzing' | 'saving' | 'success' | 'duplicate' | 'error'
  error?: string
}

interface PreparedUploadFile {
  index: number
  file: File
  fileKey: string
  sourcePath: string
  fileFingerprint: string | null
}

function buildAnalyzedMedicalDocumentPayload(
  file: File,
  patientId: string,
  storagePath: string,
  analysisResult: DocumentAnalysisResult,
  fileFingerprint: string | null
) {
  const sourcePath = getMedicalDocumentSourcePath(file)
  const analysisMetadata = buildDocumentAnalysisMetadata(analysisResult)
  const resolvedDates = resolveAnalysisDates({
    fileName: sourcePath,
    typeDocument: analysisResult.type_document,
    dateDocument: analysisResult.date_document,
    datePrelevement: analysisResult.date_prelevement
  })

  return {
    patient_id: patientId,
    document_type: analysisResult.type_document,
    title: file.name.replace(/\.[^/.]+$/, ''),
    description: analysisResult.resume || null,
    file_url: storagePath,
    file_name: file.name,
    file_size_bytes: file.size,
    mime_type: file.type || 'application/pdf',
    document_date: resolvedDates.storedDocumentDate,
    practitioner_name: analysisResult.medecin,
    medical_facility: analysisResult.laboratoire,
    specialty: analysisMetadata.inferred_specialty,
    extracted_data: {
      ...analysisResult,
      ...analysisMetadata,
      file_fingerprint_sha256: fileFingerprint,
      duplicate_signature_v1: buildMedicalDocumentDuplicateSignature(file.name, file.size),
      original_file_name: file.name,
      source_relative_path: file.webkitRelativePath || null,
      date_document: resolvedDates.dateDocument,
      date_prelevement: resolvedDates.datePrelevement
    } as unknown as Record<string, unknown>,
    tags: [],
    is_urgent: false,
    is_confidential: true,
    shared_with_doctors: []
  }
}

function buildAnalyzedMedicalDocumentUpdate(
  fileName: string,
  fileSize: number | null,
  mimeType: string | null,
  storagePath: string,
  analysisResult: DocumentAnalysisResult,
  previousExtractedData?: Record<string, unknown> | null
) {
  const analysisMetadata = buildDocumentAnalysisMetadata(analysisResult)
  const resolvedDates = resolveAnalysisDates({
    fileName,
    typeDocument: analysisResult.type_document,
    dateDocument: analysisResult.date_document,
    datePrelevement: analysisResult.date_prelevement
  })

  return {
    document_type: analysisResult.type_document,
    title: fileName.replace(/\.[^/.]+$/, ''),
    description: analysisResult.resume || null,
    file_url: storagePath,
    file_name: fileName,
    file_size_bytes: fileSize,
    mime_type: mimeType || 'application/pdf',
    document_date: resolvedDates.storedDocumentDate,
    practitioner_name: analysisResult.medecin,
    medical_facility: analysisResult.laboratoire,
    specialty: analysisMetadata.inferred_specialty,
    extracted_data: {
      ...analysisResult,
      ...analysisMetadata,
      file_fingerprint_sha256: getStoredMedicalDocumentFingerprint(previousExtractedData),
      duplicate_signature_v1:
        typeof previousExtractedData?.duplicate_signature_v1 === 'string'
          ? previousExtractedData.duplicate_signature_v1
          : buildMedicalDocumentDuplicateSignature(fileName, fileSize),
      source_relative_path:
        typeof previousExtractedData?.source_relative_path === 'string'
          ? previousExtractedData.source_relative_path
          : null,
      date_document: resolvedDates.dateDocument,
      date_prelevement: resolvedDates.datePrelevement
    } as unknown as Record<string, unknown>,
    is_urgent: false,
    is_confidential: true
  }
}

function shouldReanalyzeExistingDocument(document: {
  document_type?: string | null
  extracted_data?: Record<string, unknown> | null
  file_url?: string | null
}): boolean {
  if (!document.file_url) {
    return false
  }

  const extractedData = document.extracted_data

  if (!extractedData || Object.keys(extractedData).length === 0) {
    return true
  }

  const extractedType =
    typeof extractedData.type_document === 'string'
      ? extractedData.type_document
      : document.document_type || 'other'

  const resume = typeof extractedData.resume === 'string' ? extractedData.resume.toLowerCase() : ''
  const recommandations =
    typeof extractedData.recommandations === 'string' ? extractedData.recommandations.toLowerCase() : ''

  const analyses = Array.isArray(extractedData.analyses) ? extractedData.analyses : []
  const prescriptions = Array.isArray(extractedData.prescriptions) ? extractedData.prescriptions : []
  const diagnostics = Array.isArray(extractedData.diagnostics) ? extractedData.diagnostics : []

  const hasClinicalContext = Boolean(
    extractedData.medecin ||
    extractedData.laboratoire ||
    extractedData.date_prelevement
  )
  const analysisConfidence =
    typeof extractedData.analysis_confidence_label === 'string'
      ? extractedData.analysis_confidence_label
      : null
  const reviewRequired = extractedData.analysis_review_required === true
  const missingFields = Array.isArray(extractedData.analysis_missing_fields)
    ? extractedData.analysis_missing_fields
    : []
  const hasShortSummary = typeof extractedData.summary_short === 'string' && extractedData.summary_short.trim().length > 0

  const hasStructuredArtifacts =
    analyses.length > 0 ||
    prescriptions.length > 0 ||
    diagnostics.length > 0

  const partialMarkers = [
    'analyse partielle',
    'verification manuelle',
    'sans analyse detaillee',
    'temporairement indisponible',
    'erreur lors de l analyse'
  ]

  const hasPartialMarker = partialMarkers.some((marker) =>
    resume.includes(marker) || recommandations.includes(marker)
  )

  if (hasPartialMarker) {
    return true
  }

  if (reviewRequired || analysisConfidence === 'low' || missingFields.length > 0 || !hasShortSummary) {
    return true
  }

  if (hasStructuredArtifacts || hasClinicalContext) {
    return false
  }

  return ['blood_test', 'prescription', 'other'].includes(extractedType)
}

async function loadExistingMedicalDocumentDuplicateSignals(patientId: string): Promise<{
  fingerprintSet: Set<string>
  fallbackSignatureSet: Set<string>
}> {
  const { data, error } = await supabase
    .from('medical_documents')
    .select('file_name, file_size_bytes, extracted_data')
    .eq('patient_id', patientId)

  if (error) {
    throw new Error(`Impossible de verifier les doublons existants: ${error.message}`)
  }

  const fingerprintSet = new Set<string>()
  const fallbackSignatureSet = new Set<string>()

  for (const document of data || []) {
    if (isIgnoredMedicalDocumentRecord(document)) {
      continue
    }

    const extractedData =
      document.extracted_data && typeof document.extracted_data === 'object' && !Array.isArray(document.extracted_data)
        ? (document.extracted_data as Record<string, unknown>)
        : null

    const fingerprint = getStoredMedicalDocumentFingerprint(extractedData)

    if (fingerprint) {
      fingerprintSet.add(fingerprint)
      continue
    }

    const fallbackSignature = buildMedicalDocumentDuplicateSignature(
      document.file_name,
      document.file_size_bytes
    )

    if (fallbackSignature) {
      fallbackSignatureSet.add(fallbackSignature)
    }
  }

  return {
    fingerprintSet,
    fallbackSignatureSet
  }
}

async function saveAnalysisArtifacts(
  documentId: string,
  patientId: string,
  analysisResult: DocumentAnalysisResult
) {
  const resolvedDates = resolveAnalysisDates({
    fileName: '',
    typeDocument: analysisResult.type_document,
    dateDocument: analysisResult.date_document,
    datePrelevement: analysisResult.date_prelevement
  })
  const analysisDate = resolvedDates.datePrelevement || resolvedDates.dateDocument

  await Promise.allSettled([
    supabase.from('prescriptions').delete().eq('document_id', documentId),
    supabase.from('analyses_resultats').delete().eq('document_id', documentId),
    supabase.from('diagnostics').delete().eq('document_id', documentId)
  ])

  if (analysisResult.prescriptions.length > 0) {
    const prescriptions: PrescriptionInput[] = analysisResult.prescriptions.map((prescription) => ({
      document_id: documentId,
      patient_id: patientId,
      medicament: prescription.medicament,
      dosage: prescription.dosage || null,
      frequence: prescription.frequence || null,
      duree: prescription.duree || null,
      date_prescription: resolvedDates.dateDocument,
      medecin: analysisResult.medecin,
      instructions: prescription.instructions || null,
      statut: 'actif'
    }))

    const { error } = await supabase.from('prescriptions').insert(prescriptions)
    if (error) {
      console.error('Erreur creation prescriptions:', error)
    }
  }

  if (analysisResult.analyses.length > 0) {
    const analyses: AnalyseResultatInput[] = buildAnalyseResultatRows({
      documentId,
      patientId,
      analysisDate,
      analysisResult
    })

    const { error } = await supabase.from('analyses_resultats').insert(analyses)
    if (error) {
      console.error('Erreur creation analyses:', error)
    } else {
      await syncPatientLabTestCatalog({
        documentId,
        patientId,
        analysisDate,
        analysisResult
      })
    }
  }

  if (analysisResult.diagnostics.length > 0) {
    const diagnostics: DiagnosticInput[] = analysisResult.diagnostics.map((diagnostic) => ({
      document_id: documentId,
      patient_id: patientId,
      diagnostic: diagnostic.diagnostic,
      type_diagnostic: diagnostic.type_diagnostic || 'principal',
      code_cim10: null,
      medecin: analysisResult.medecin,
      specialite: null,
      date_diagnostic: resolvedDates.dateDocument,
      gravite: diagnostic.gravite || null,
      commentaire: null
    }))

    const { error } = await supabase.from('diagnostics').insert(diagnostics)
    if (error) {
      console.error('Erreur creation diagnostics:', error)
    }
  }
}

/**
 * Upload d'un document medical dans le bucket prive puis insertion en base.
 * On stocke le chemin Storage dans file_url pour signer l'URL a la lecture.
 */
export async function simpleUploadDocument(
  file: File,
  patientId: string,
  onProgress?: (progress: SimpleUploadProgress) => void,
  options?: {
    fileFingerprint?: string | null
    skipDuplicateCheck?: boolean
  }
): Promise<SimpleUploadResult> {
  const fileKey = getMedicalDocumentFileKey(file)
  const sourcePath = getMedicalDocumentSourcePath(file)

  const updateProgress = (update: Partial<SimpleUploadProgress>) => {
    if (!onProgress) {
      return
    }

    onProgress({
      fileKey,
      fileName: sourcePath,
      progress: 0,
      status: 'pending',
      ...update
    })
  }

  let storagePath: string | undefined

  try {
    if (isIgnoredMedicalDocumentFileCandidate(file)) {
      updateProgress({
        status: 'error',
        progress: 0,
        error: 'Fichier systeme macOS ignore'
      })

      return {
        success: false,
        error: 'Fichier systeme macOS ignore'
      }
    }

    const fileFingerprint =
      options?.fileFingerprint === undefined
        ? await computeMedicalDocumentFingerprint(file)
        : options.fileFingerprint
    const fallbackSignature = buildMedicalDocumentDuplicateSignature(file.name, file.size)

    if (!options?.skipDuplicateCheck) {
      updateProgress({ status: 'checking', progress: 5 })

      const { fingerprintSet, fallbackSignatureSet } =
        await loadExistingMedicalDocumentDuplicateSignals(patientId)

      if (fileFingerprint && fingerprintSet.has(fileFingerprint)) {
        updateProgress({
          status: 'duplicate',
          progress: 100,
          error: 'Document deja importe'
        })

        return {
          success: true,
          duplicate: true,
          error: 'Document deja importe'
        }
      }

      if (!fileFingerprint && fallbackSignature && fallbackSignatureSet.has(fallbackSignature)) {
        updateProgress({
          status: 'duplicate',
          progress: 100,
          error: 'Document deja importe (nom et taille identiques)'
        })

        return {
          success: true,
          duplicate: true,
          error: 'Document deja importe (nom et taille identiques)'
        }
      }
    }

    updateProgress({ status: 'uploading', progress: 10 })

    const uploadResult = await uploadMedicalDocumentToPrivateBucket(file, patientId)
    storagePath = uploadResult.storagePath

    updateProgress({ status: 'analyzing', progress: 45 })

    const analysisSourceUrl =
      uploadResult.signedUrl || (await createSignedMedicalDocumentUrl(uploadResult.storagePath))
    const analysisResult = await analyzeDocumentWithClaude(analysisSourceUrl, sourcePath)

    updateProgress({ status: 'saving', progress: 80 })

    const { data: document, error: dbError } = await supabase
      .from('medical_documents')
      .insert(
        buildAnalyzedMedicalDocumentPayload(
          file,
          patientId,
          storagePath,
          analysisResult,
          fileFingerprint || null
        )
      )
      .select()
      .single()

    if (dbError) {
      throw new Error(`Database error: ${dbError.message}`)
    }

    await saveAnalysisArtifacts(document.id, patientId, analysisResult)

    updateProgress({ status: 'success', progress: 100 })

    return {
      success: true,
      documentId: document.id,
      storagePath
    }
  } catch (error) {
    if (storagePath) {
      await removeMedicalDocumentFromPrivateBucket(storagePath)
    }

    const message = error instanceof Error ? error.message : 'Unknown upload error'

    console.error('Upload error:', error)
    updateProgress({ status: 'error', progress: 0, error: message })

    return {
      success: false,
      storagePath,
      error: message
    }
  }
}

/**
 * Upload de plusieurs fichiers avec concurrence limitee pour eviter de saturer Storage.
 */
export async function simpleUploadMultiple(
  files: File[],
  patientId: string,
  onFileProgress?: (fileKey: string, progress: SimpleUploadProgress) => void,
  options?: {
    concurrency?: number
  }
): Promise<SimpleUploadResult[]> {
  if (files.length === 0) {
    return []
  }

  const results: SimpleUploadResult[] = new Array(files.length)
  const { fingerprintSet: existingFingerprintSet, fallbackSignatureSet: existingFallbackSignatureSet } =
    await loadExistingMedicalDocumentDuplicateSignals(patientId)
  const currentBatchFingerprintSet = new Set<string>()
  const currentBatchFallbackSignatureSet = new Set<string>()
  const preparedFiles: PreparedUploadFile[] = []
  const concurrency = Math.max(
    1,
    Math.min(options?.concurrency ?? DEFAULT_MEDICAL_DOCUMENT_UPLOAD_CONCURRENCY, files.length)
  )

  for (const [index, file] of files.entries()) {
    const fileKey = getMedicalDocumentFileKey(file)
    const sourcePath = getMedicalDocumentSourcePath(file)

    if (isIgnoredMedicalDocumentFileCandidate(file)) {
      results[index] = {
        success: true,
        duplicate: true,
        error: 'Fichier systeme ignore'
      }

      onFileProgress?.(fileKey, {
        fileKey,
        fileName: sourcePath,
        progress: 100,
        status: 'duplicate',
        error: 'Fichier systeme ignore'
      })
      continue
    }

    onFileProgress?.(fileKey, {
      fileKey,
      fileName: sourcePath,
      progress: 5,
      status: 'checking'
    })

    const fileFingerprint = await computeMedicalDocumentFingerprint(file)
    const fallbackSignature = buildMedicalDocumentDuplicateSignature(file.name, file.size)

    if (fileFingerprint && existingFingerprintSet.has(fileFingerprint)) {
      results[index] = {
        success: true,
        duplicate: true,
        error: 'Document deja importe'
      }

      onFileProgress?.(fileKey, {
        fileKey,
        fileName: sourcePath,
        progress: 100,
        status: 'duplicate',
        error: 'Document deja importe'
      })
      continue
    }

    if (fileFingerprint && currentBatchFingerprintSet.has(fileFingerprint)) {
      results[index] = {
        success: true,
        duplicate: true,
        error: 'Doublon detecte dans cette selection'
      }

      onFileProgress?.(fileKey, {
        fileKey,
        fileName: sourcePath,
        progress: 100,
        status: 'duplicate',
        error: 'Doublon detecte dans cette selection'
      })
      continue
    }

    if (!fileFingerprint && fallbackSignature && existingFallbackSignatureSet.has(fallbackSignature)) {
      results[index] = {
        success: true,
        duplicate: true,
        error: 'Document deja importe (nom et taille identiques)'
      }

      onFileProgress?.(fileKey, {
        fileKey,
        fileName: sourcePath,
        progress: 100,
        status: 'duplicate',
        error: 'Document deja importe (nom et taille identiques)'
      })
      continue
    }

    if (!fileFingerprint && fallbackSignature && currentBatchFallbackSignatureSet.has(fallbackSignature)) {
      results[index] = {
        success: true,
        duplicate: true,
        error: 'Doublon detecte dans cette selection'
      }

      onFileProgress?.(fileKey, {
        fileKey,
        fileName: sourcePath,
        progress: 100,
        status: 'duplicate',
        error: 'Doublon detecte dans cette selection'
      })
      continue
    }

    if (fileFingerprint) {
      existingFingerprintSet.add(fileFingerprint)
      currentBatchFingerprintSet.add(fileFingerprint)
    } else if (fallbackSignature) {
      currentBatchFallbackSignatureSet.add(fallbackSignature)
    }

    preparedFiles.push({
      index,
      file,
      fileKey,
      sourcePath,
      fileFingerprint
    })
  }

  let nextIndex = 0

  const runWorker = async () => {
    while (true) {
      const currentIndex = nextIndex
      nextIndex += 1

      if (currentIndex >= preparedFiles.length) {
        return
      }

      const currentFile = preparedFiles[currentIndex]

      results[currentFile.index] = await simpleUploadDocument(
        currentFile.file,
        patientId,
        onFileProgress ? (progress) => onFileProgress(currentFile.fileKey, progress) : undefined,
        {
          fileFingerprint: currentFile.fileFingerprint,
          skipDuplicateCheck: true
        }
      )
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => runWorker()))

  return results
}

export async function backfillMedicalDocumentAnalysis(
  patientId: string
): Promise<{ analyzed: number; skipped: number; failed: Array<{ fileName: string; error: string }> }> {
  const { data: documents, error } = await supabase
    .from('medical_documents')
    .select('id, patient_id, document_type, file_name, file_url, file_size_bytes, mime_type, extracted_data')
    .eq('patient_id', patientId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Impossible de charger les documents a analyser: ${error.message}`)
  }

  let analyzed = 0
  let skipped = 0
  const failed: Array<{ fileName: string; error: string }> = []

  for (const document of documents || []) {
    if (isIgnoredMedicalDocumentRecord(document)) {
      skipped += 1
      continue
    }

    const extractedData = document.extracted_data as Record<string, unknown> | null
    const shouldAnalyze = shouldReanalyzeExistingDocument({
      document_type: document.document_type,
      extracted_data: extractedData,
      file_url: document.file_url
    })

    if (!shouldAnalyze) {
      skipped += 1
      continue
    }

    try {
      const analysisSourceUrl = await resolveMedicalDocumentUrl(document.file_url)

      if (!analysisSourceUrl) {
        throw new Error('Impossible de resoudre l URL du document a analyser')
      }

      const analysisResult = await analyzeDocumentWithClaude(analysisSourceUrl, document.file_name)

      const { error: updateError } = await supabase
        .from('medical_documents')
        .update(
          buildAnalyzedMedicalDocumentUpdate(
            document.file_name,
            document.file_size_bytes,
            document.mime_type,
            document.file_url,
            analysisResult,
            extractedData
          )
        )
        .eq('id', document.id)
        .eq('patient_id', patientId)

      if (updateError) {
        throw new Error(updateError.message)
      }

      await saveAnalysisArtifacts(document.id, patientId, analysisResult)
      analyzed += 1
    } catch (analysisError) {
      failed.push({
        fileName: document.file_name,
        error: analysisError instanceof Error ? analysisError.message : 'Erreur inconnue'
      })
    }
  }

  return { analyzed, skipped, failed }
}
